import React, { useMemo } from 'react';
import { View, Text, StyleSheet, FlatList } from 'react-native';
import { usePaymentDesignTokens, PAYMENT_TYPOGRAPHY, PAYMENT_SPACING } from '@/constants/residents/mobile/paymentDesignTokens';
import { mobilePaymentsStrings } from '@/constants/residents/strings';
import { formatCurrency } from '@/utils/common/formatting';
import { PaymentBottomSheet } from './PaymentBottomSheet';

export interface RecurringCharge {
  name: string;
  amount: number | null; // null for TBD
  frequency: string;
}

interface RecurringChargesModalProps {
  visible: boolean;
  onClose: () => void;
  charges: RecurringCharge[];
}

/**
 * Recurring Charges Modal
 * Bottom sheet showing scheduled/recurring charges
 * Matches Figma design 2.a. Show recurring charges
 */
export const RecurringChargesModal: React.FC<RecurringChargesModalProps> = ({
  visible,
  onClose,
  charges,
}) => {
  const pt = usePaymentDesignTokens();

  const styles = useMemo(() => StyleSheet.create({
    chargeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: PAYMENT_SPACING.md,
    },
    chargeName: {
      flex: 1,
      ...PAYMENT_TYPOGRAPHY.bodySm,
      color: pt.TEXT_PRIMARY,
    },
    chargeAmount: {
      ...PAYMENT_TYPOGRAPHY.bodySm,
      color: pt.TEXT_PRIMARY,
      marginRight: PAYMENT_SPACING.xxl,
      minWidth: 80,
      textAlign: 'right',
    },
    chargeFrequency: {
      ...PAYMENT_TYPOGRAPHY.bodySm,
      color: pt.TEXT_PRIMARY,
      minWidth: 60,
    },
    separator: {
      height: 1,
      backgroundColor: pt.BORDER,
    },
    emptyText: {
      ...PAYMENT_TYPOGRAPHY.bodySm,
      color: pt.TEXT_MUTED,
      textAlign: 'center',
      paddingVertical: PAYMENT_SPACING.xl,
    },
}), [pt]);

  const renderChargeRow = ({ item }: { item: RecurringCharge }) => (
    <View style={styles.chargeRow}>
      <Text style={styles.chargeName}>{item.name}</Text>
      <Text style={styles.chargeAmount}>
        {item.amount !== null ? formatCurrency(item.amount) : mobilePaymentsStrings.tbd}
      </Text>
      <Text style={styles.chargeFrequency}>{item.frequency}</Text>
    </View>
  );

  return (
    <PaymentBottomSheet
      visible={visible}
      onClose={onClose}
      title={mobilePaymentsStrings.recurringCharges}
          >
        {charges.length > 0 ? (
          <FlatList
            data={charges}
            renderItem={renderChargeRow}
            keyExtractor={(item, index) => `${item.name}-${index}`}
            scrollEnabled={false}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
          />
        ) : (
          <Text style={styles.emptyText}>No recurring charges scheduled.</Text>
        )}
    </PaymentBottomSheet>
  );
};

